import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ModalWrapper, ModalCloseButton, TextBox, Select } from '../common';
import { shoppingService } from '../../api/services';
import { formatPrice } from '../../utils/formatPrice';
import { vbankBanks } from '../../constants/uiConstants';

// 가상계좌 계좌번호/예금주 input — 은행 드롭다운(Select size="sm")과 동일한 룩으로 통일.
const ACCOUNT_INPUT_CLS = 'w-full h-8 px-3 text-xs bg-white border border-mono-200 rounded-lg text-mono-900 placeholder:text-mono-400 hover:border-mono-300 focus:border-orange-500 focus:outline-none transition';

/**
 * 고객 프라이빗 쇼핑 부분환불 — '환불/취소 요청' 버튼 + 요청 모달 + 반려사유 배너.
 * 실제 환불은 관리자 승인 시 실행 (여기서는 요청만 생성). 가상계좌 결제건은 환불받을 계좌정보 필수.
 * 다국어(ko/en/ja/zh/es): reservation.shoppingRefund.* + common.*
 */
export default function ShoppingRefundRequest({ orderId, guestEmail }) {
  const { t } = useTranslation();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const load = useCallback(() => {
    if (!orderId) { setLoading(false); return; }
    setLoading(true);
    shoppingService.getOrderById(orderId, guestEmail)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [orderId, guestEmail]);

  useEffect(() => { load(); }, [load]);

  if (loading || !detail) return null;

  const status = detail.status;
  const isRequested = status === 'refund_requested';
  const isDone = ['fully_refunded', 'cancelled'].includes(status);
  // 반려된 '품목'은 재요청 불가(다른 품목은 가능). 반려 배너는 정보용으로 함께 노출.
  const rejectedRec = (detail.refunds || []).find((r) => r.type === 'customer_request' && r.status === 'rejected');
  const hasRejected = !!rejectedRec;
  const rejectedReason = rejectedRec?.rejection_reason || detail.latest_rejection_reason;
  const rejectedItemIds = new Set(
    (detail.refunds || [])
      .filter((r) => r.type === 'customer_request' && r.status === 'rejected')
      .flatMap((r) => (r.items || []).map((i) => i.item_id))
  );
  // 요청 가능 품목 = 잔여수량 있고 + 반려된 적 없는 품목
  const canRequest = (detail.items || []).some((it) => (it.refundable_quantity || 0) > 0 && !rejectedItemIds.has(it.id));

  return (
    <div className="mt-3 pt-3 border-t border-mono-100">
      {hasRejected && (
        <div className="mb-2 rounded-lg bg-red-50 border border-red-200 p-3">
          <p className="text-2sm font-bold text-red-700">{t('reservation.shoppingRefund.rejectedTitle')}</p>
          {rejectedReason && <p className="text-2sm text-red-600 mt-0.5">{rejectedReason}</p>}
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-2sm text-mono-500">
          {isRequested ? t('reservation.shoppingRefund.reviewing')
            : isDone ? t('reservation.shoppingRefund.done')
            : t('reservation.shoppingRefund.available')}
        </p>
        {!isRequested && !isDone && canRequest && (
          <Button level="level3" size="sm" onClick={() => { setSubmitted(false); setOpen(true); }}>
            {t('reservation.shoppingRefund.requestButton')}
          </Button>
        )}
      </div>

      {open && (
        <RefundRequestModal
          detail={detail}
          guestEmail={guestEmail}
          excludeItemIds={rejectedItemIds}
          onClose={() => setOpen(false)}
          onDone={() => { setOpen(false); setSubmitted(true); load(); }}
        />
      )}
      {submitted && (
        <ModalWrapper onClose={() => setSubmitted(false)} variant="compact" maxWidth="max-w-[400px]">
          <div className="text-center space-y-2 py-2">
            <p className="text-lg font-bold text-mono-900">{t('reservation.shoppingRefund.submittedTitle')}</p>
            <p className="text-2sm text-mono-500">{t('reservation.shoppingRefund.submittedDesc')}</p>
            <Button level="level1" onClick={() => setSubmitted(false)}>{t('common.confirm')}</Button>
          </div>
        </ModalWrapper>
      )}
    </div>
  );
}

function RefundRequestModal({ detail, guestEmail, excludeItemIds, onClose, onDone }) {
  const { t } = useTranslation();
  const won = (v) => `${formatPrice(Number(v) || 0)} ${t('common.won')}`;
  // 잔여수량 있고 + 반려된 적 없는 품목만 노출 (반려된 같은 상품 재요청 차단)
  const items = (detail.items || []).filter((it) => (it.refundable_quantity || 0) > 0 && !(excludeItemIds && excludeItemIds.has(it.id)));
  const [qty, setQty] = useState(() => Object.fromEntries(items.map((it) => [it.id, 0])));
  const [note, setNote] = useState('');
  const [account, setAccount] = useState({ bank: '', number: '', holder: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const isVbank = detail.pay_method === 'vbank';

  const rows = useMemo(() => items.map((it) => {
    const q = Math.max(0, Math.min(it.refundable_quantity, Number(qty[it.id] || 0)));
    return { ...it, q, amount: (Number(it.unit_price) || 0) * q };
  }), [items, qty]);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const selected = rows.filter((r) => r.q > 0).map((r) => ({ item_id: r.id, quantity: r.q }));

  const submit = async () => {
    if (selected.length === 0) { setError(t('reservation.shoppingRefund.errorSelectQty')); return; }
    if (isVbank && !account.number) { setError(t('reservation.shoppingRefund.errorVbankAccount')); return; }
    setSubmitting(true); setError(null);
    try {
      await shoppingService.requestRefund(detail.id, {
        items: selected, note: note || null,
        refund_account: isVbank ? account : null,
      }, guestEmail);
      onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || t('reservation.shoppingRefund.errorFailed'));
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper onClose={onClose} variant="compact" maxWidth="max-w-[560px]">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-mono-900">{t('reservation.shoppingRefund.requestButton')}</h2>
        <ModalCloseButton onClick={onClose} />
      </div>
      <p className="text-2sm text-mono-500">{t('reservation.shoppingRefund.modalDesc')}</p>

      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-2sm font-medium text-mono-900 truncate">{r.name || t('reservation.shoppingRefund.productFallback', { id: r.shopping_id })}</p>
              <p className="text-2sm text-mono-400">{won(r.unit_price)} · {t('reservation.shoppingRefund.remaining', { count: r.refundable_quantity })}</p>
            </div>
            <input
              type="number" min={0} max={r.refundable_quantity}
              className="w-16 text-center border border-mono-300 rounded px-1 py-1"
              value={qty[r.id]}
              onChange={(e) => setQty({ ...qty, [r.id]: e.target.value })}
            />
            <span className="w-24 text-right text-2sm font-bold text-mono-900">{won(r.amount)}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between border-t border-mono-200 pt-2">
        <span className="text-sm font-medium text-mono-600">{t('reservation.shoppingRefund.expectedAmount')}</span>
        <span className="text-base font-bold text-red-600">{won(total)}</span>
      </div>

      {isVbank && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-2">
          <p className="text-2sm font-bold text-amber-700">{t('reservation.shoppingRefund.vbankAccountTitle')}</p>
          <div className="grid grid-cols-3 gap-2">
            <Select value={account.bank} onChange={(v) => setAccount({ ...account, bank: v })} options={vbankBanks} placeholder={t('reservation.shoppingRefund.bank')} size="sm" />
            <input className={ACCOUNT_INPUT_CLS} placeholder={t('reservation.shoppingRefund.accountNumber')} value={account.number} inputMode="numeric" onChange={(e) => setAccount({ ...account, number: e.target.value.replace(/[^0-9]/g, '') })} />
            <input className={ACCOUNT_INPUT_CLS} placeholder={t('reservation.shoppingRefund.accountHolder')} value={account.holder} onChange={(e) => setAccount({ ...account, holder: e.target.value })} />
          </div>
        </div>
      )}

      <TextBox label={t('reservation.shoppingRefund.reasonLabel')} placeholder={t('reservation.shoppingRefund.reasonPlaceholder')} value={note} onChange={(e) => setNote(e.target.value)} bg="white" />

      {error && <p className="text-2sm text-red-500">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button level="level3" onClick={onClose}>{t('common.cancel')}</Button>
        <Button level="level1" onClick={submit} disabled={submitting || total <= 0}>
          {submitting ? t('reservation.shoppingRefund.submitting') : t('reservation.shoppingRefund.requestButton')}
        </Button>
      </div>
    </ModalWrapper>
  );
}
